import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { 
  Users, 
  Stethoscope, 
  Calendar, 
  Target, 
  CheckSquare, 
  ArrowRight,
  TrendingUp,
  Clock,
  Sprout
} from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await apiClient.get("/dashboard/stats");
      setStats(response.data);
    } catch (error) {
      toast.error("Failed to load dashboard");
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-muted rounded-lg w-48" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-32 bg-muted rounded-3xl" />)}
        </div>
      </div>
    );
  }

  const statCards = [
    { 
      label: "Children", 
      value: stats?.children_count || 0, 
      icon: Users, 
      color: "bg-teal-100 text-teal-700",
      path: "/children"
    },
    { 
      label: "Therapists", 
      value: stats?.providers_count || 0, 
      icon: Stethoscope, 
      color: "bg-golden-100 text-golden-500",
      path: "/team/therapists"
    },
    { 
      label: "Upcoming", 
      value: stats?.upcoming_appointments || 0, 
      icon: Calendar, 
      color: "bg-coral-100 text-coral-500",
      path: "/appointments"
    },
    { 
      label: "Active Goals", 
      value: stats?.active_goals || 0, 
      icon: Target, 
      color: "bg-info/10 text-info",
      path: "/goals"
    },
  ];

  // Mock progress data for chart
  const progressData = [
    { name: 'Week 1', progress: 20 },
    { name: 'Week 2', progress: 35 },
    { name: 'Week 3', progress: 45 },
    { name: 'Week 4', progress: 60 },
    { name: 'Week 5', progress: 72 },
    { name: 'Week 6', progress: 85 },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Welcome Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-primary">
            Welcome Back!
          </h1>
          <p className="text-muted-foreground mt-1">Here's your family's progress overview</p>
        </div>
        <Button 
          onClick={() => navigate("/team/children")}
          className="rounded-full"
          data-testid="add-child-quick-btn"
        >
          <Sprout className="mr-2 h-4 w-4" />
          Add Child Profile
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, i) => (
          <Card 
            key={stat.label}
            className={`border-0 shadow-lg rounded-3xl cursor-pointer card-hover animate-fadeIn stagger-${i + 1}`}
            onClick={() => navigate(stat.path)}
            data-testid={`stat-${stat.label.toLowerCase()}`}
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className={`h-12 w-12 rounded-2xl ${stat.color} flex items-center justify-center`}>
                  <stat.icon className="h-6 w-6" />
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className="text-3xl font-heading font-bold">{stat.value}</p>
              <p className="text-sm text-muted-foreground">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Progress Chart */}
        <Card className="border-0 shadow-lg rounded-3xl">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-success" />
              Overall Progress
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={progressData}>
                  <defs>
                    <linearGradient id="progressGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip 
                    contentStyle={{ 
                      borderRadius: '12px', 
                      border: 'none', 
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)' 
                    }} 
                  />
                  <Area 
                    type="monotone" 
                    dataKey="progress" 
                    stroke="hsl(var(--primary))" 
                    fill="url(#progressGradient)"
                    strokeWidth={3}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Goals Progress */}
        <Card className="border-0 shadow-lg rounded-3xl">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading flex items-center gap-2">
                <Target className="h-5 w-5 text-primary" />
                Active Goals
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                className="rounded-full"
                onClick={() => navigate("/planner/goals")}
              >
                View All
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {stats?.goals_progress?.length > 0 ? (
              stats.goals_progress.slice(0, 4).map((goal, i) => (
                <div key={i} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium truncate max-w-[200px]">{goal.title}</span>
                    <span className="text-muted-foreground">{goal.progress}%</span>
                  </div>
                  <Progress value={goal.progress} className="h-2 rounded-full" />
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Target className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No active goals yet</p>
                <Button 
                  variant="link" 
                  className="mt-2"
                  onClick={() => navigate("/planner/goals")}
                >
                  Create your first goal
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Upcoming Appointments */}
        <Card className="border-0 shadow-lg rounded-3xl">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading flex items-center gap-2">
                <Calendar className="h-5 w-5 text-coral-500" />
                Upcoming Sessions
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                className="rounded-full"
                onClick={() => navigate("/planner/calendar")}
              >
                View All
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {stats?.recent_appointments?.length > 0 ? (
              stats.recent_appointments.slice(0, 4).map((appt, i) => (
                <div 
                  key={i} 
                  className="flex items-center gap-4 p-3 bg-muted/30 rounded-2xl hover:bg-muted/50 transition-colors"
                >
                  <div className="h-10 w-10 rounded-xl bg-coral-100 flex items-center justify-center">
                    <Clock className="h-5 w-5 text-coral-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{appt.title}</p>
                    <p className="text-sm text-muted-foreground">
                      {appt.date} at {appt.time}
                    </p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    appt.status === 'scheduled' 
                      ? 'bg-info/10 text-info' 
                      : 'bg-success/10 text-success'
                  }`}>
                    {appt.status}
                  </span>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Calendar className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No upcoming appointments</p>
                <Button 
                  variant="link" 
                  className="mt-2"
                  onClick={() => navigate("/planner/calendar")}
                >
                  Schedule a session
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pending Activities */}
        <Card className="border-0 shadow-lg rounded-3xl">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading flex items-center gap-2">
                <CheckSquare className="h-5 w-5 text-golden-500" />
                Today's Activities
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                className="rounded-full"
                onClick={() => navigate("/planner/activities")}
              >
                View All
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {stats?.recent_activities?.length > 0 ? (
              stats.recent_activities.slice(0, 4).map((activity, i) => (
                <div 
                  key={i} 
                  className="flex items-center gap-4 p-3 bg-muted/30 rounded-2xl hover:bg-muted/50 transition-colors"
                >
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${
                    activity.priority === 'high' 
                      ? 'bg-coral-100' 
                      : activity.priority === 'medium'
                        ? 'bg-golden-100'
                        : 'bg-teal-100'
                  }`}>
                    <CheckSquare className={`h-5 w-5 ${
                      activity.priority === 'high' 
                        ? 'text-coral-500' 
                        : activity.priority === 'medium'
                          ? 'text-golden-500'
                          : 'text-teal-700'
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{activity.title}</p>
                    <p className="text-sm text-muted-foreground capitalize">{activity.category}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full capitalize ${
                    activity.priority === 'high' 
                      ? 'bg-coral-100 text-coral-500' 
                      : activity.priority === 'medium'
                        ? 'bg-golden-100 text-golden-500'
                        : 'bg-teal-100 text-teal-700'
                  }`}>
                    {activity.priority}
                  </span>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <CheckSquare className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No pending activities</p>
                <Button 
                  variant="link" 
                  className="mt-2"
                  onClick={() => navigate("/planner/activities")}
                >
                  Add an activity
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
